import React, { useEffect, useState } from 'react';
import { Form, Header, Message, Segment, Table } from 'semantic-ui-react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBlockUI } from '../../../../shared/services/block-ui-service';
import { ToasterConfig } from '../../../../config/ToasterConfig';
import { UpdateVehiclePlateRequest } from '../../../../models/update-vehicle-plate-request';
import useVehiclePlateService from '../../../../services/backoffice/hooks/useVehiclePlateService';
import { ShortDto } from '../../../../models/short-dto';
import useClientService from '../../../../services/backoffice/hooks/useClientService';
import useVehicleTypeService from '../../../../services/backoffice/hooks/useVehicleTypeService';
import useFleetTypeService from '../../../../services/backoffice/hooks/useFleetTypeService';

export async function editVehiclePlateLoader({ params }: any) {
    return params.id;
}

const EditVehiclePlatePage = () => {
    const [plate, setPlate] = useState<Partial<UpdateVehiclePlateRequest>>();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const navigate = useNavigate();
    const loader: any = useLoaderData();
    const blockUI = useBlockUI();
    
    const { getVehiclePlateById, editVehiclePlate } = useVehiclePlateService();
    const [clientes, setClients] = useState<ShortDto[]>([]);
    const [vehicleTypes, setVehicleTypes] = useState<ShortDto[]>([]);
    const [fleetTypes, setFleetTypes] = useState<ShortDto[]>([]);
    
    const { getAllClientsShortDto } = useClientService();
    const { getAllVehicleTypesShortDto } = useVehicleTypeService();
    const { getAllFleetTypesShortDto } = useFleetTypeService();

    useEffect(() => {
        const plateId = loader;
        if (!plateId) return;

        getVehiclePlateById(plateId)
            .then((response: any) => {
                setPlate(response);
            })
            .catch((error) => {
                setError('Erro ao carregar a placa');
            })
            .finally(() => {
                setLoading(false);  // Quando a requisição terminar (sucesso ou erro)
            });
    }, [loader]);

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    useEffect(() => {
        async function loadData() {
            try {
                const clientsResponse = await getAllClientsShortDto();
                setClients(clientsResponse);
                
                const vehicleTypesResponse = await getAllVehicleTypesShortDto();
                setVehicleTypes(vehicleTypesResponse);
                
                const fleetTypesResponse = await getAllFleetTypesShortDto();
                setFleetTypes(fleetTypesResponse);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            }
        }

        loadData();
    }, []);

    const updateClient = async () => {
        try {
            setLoading(true);
            if (plate) {
                const result = await editVehiclePlate(plate, plate.id);
                if (!!result) {
                    navigate('/backoffice/vehicle-plates/');
                    toast.success('Placa atualizada com sucesso', ToasterConfig.success);
                }
            }
        } catch (error: any) {
            setLoading(false);
            setError(error.message);
            toast.error('Erro ao atualizar placa', ToasterConfig.error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div>Carregando...</div>;
    }

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Placas / Editar
            </Header>
            <Form size="large" onSubmit={updateClient}>
                <Segment stacked>
                    <Form.Input
                        fluid
                        label="Placa"
                        placeholder="Placa"
                        name="plate"
                        value={plate?.plate}
                        onChange={(event) => setPlate({ ...plate, plate: event.target.value })}
                        required
                    />
                    
                    <Form.Input
                        fluid
                        label="Tag"
                        placeholder="Tag do veículo"
                        name="tag"
                        value={plate?.tag}
                        onChange={(event) => setPlate({ ...plate, tag: event.target.value })}
                        required
                    />
                    
                    <Form.Input
                        fluid
                        label="Descrição"
                        placeholder="Descrição"
                        name="description"
                        value={plate?.description}
                        onChange={(event) => setPlate({ ...plate, description: event.target.value })}
                    />

                    <Form.Select
                        label={'Cliente ao qual a placa pertence'}
                        options={clientes}
                        placeholder="Selecione uma opção"
                        value={plate?.clientId ?? "-"}
                        required
                        search
                        clearable
                        onChange={(e: any, data: any) => {
                            const selectedValue = data.value;
                            setPlate(prevPlate => ({ ...prevPlate, clientId: selectedValue }));
                        }}
                    />
                    
                    <Form.Select
                        label={'Tipo de Veículo'}
                        options={vehicleTypes}
                        placeholder='Selecione um tipo de veículo'
                        value={plate?.typeId ?? "-"}
                        required
                        search
                        clearable
                        onChange={(e: any, data: any) => {
                            const selectedValue = data.value;
                            setPlate(prevPlate => ({...prevPlate, typeId: selectedValue}));
                        }}
                    />

                    <Form.Select
                        label={'Tipo de Frota'}
                        options={fleetTypes}
                        placeholder='Selecione um tipo de frota'
                        value={plate?.fleetTypeId ?? "-"}
                        required
                        search
                        clearable
                        onChange={(e: any, data: any) => {
                            const selectedValue = data.value;
                            setPlate(prevPlate => ({...prevPlate, fleetTypeId: selectedValue}));
                        }}
                    />

                    <Table.Row>
                        <Table.HeaderCell>
                            <Form.Button content="Voltar"
                                         icon="arrow left"
                                         onClick={() => navigate('/backoffice/vehicle-plates/')} grey/>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Form.Button content="Atualizar"
                                         type={"submit"}
                                         icon="save"
                                         color="green"/>
                        </Table.HeaderCell>
                    </Table.Row>
                    {error && <Message negative>{error}</Message>}
                </Segment>
            </Form>
        </div>
    );
};

export default EditVehiclePlatePage;
